import React, { useCallback } from "react";
import { Box, LinearProgress, Paper, Typography } from "@material-ui/core";
import { green, grey, blue } from "@material-ui/core/colors";
import { CloudUpload, CheckCircle } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ReferralItem } from "./types";

interface Props {
  onFilesSelected: (files: File[]) => void;
  items?: ReferralItem[];
  isProcessing?: boolean;
}

const DropZone = styled(Paper)<{ $isDragOver?: boolean }>`
  border: 2px dashed ${({ $isDragOver }) => ($isDragOver ? blue[700] : grey[400])};
  background: ${({ $isDragOver }) => ($isDragOver ? blue[50] : grey[50])};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${blue[700]};
    background: ${blue[50]};
  }
`;

export const BatchDropzone: React.VFC<Props> = ({ onFilesSelected, items, isProcessing }) => {
  const { t } = useTranslation();
  const [isDragOver, setIsDragOver] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith(".pdf"));
      if (pdfFiles.length > 0) {
        onFilesSelected(pdfFiles);
      }
    },
    [onFilesSelected]
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setIsDragOver(false);
  }, []);

  const handleClick = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleFiles(e.target.files);
    },
    [handleFiles]
  );

  if (isProcessing && items) {
    const processed = items.filter(i => i.status === "extracted").length;
    const total = items.length;
    const progress = (processed / total) * 100;

    return (
      <Paper elevation={2}>
        <Box p={6} textAlign="center">
          <Typography variant="h6" gutterBottom>
            {t("form.batch_processing")}
          </Typography>
          <Box my={3}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Typography variant="body2" color="textSecondary">
            {t("form.batch_progress", { processed, total })}
          </Typography>
          <Box mt={3}>
            {items.map(item => (
              <Box key={item.id} display="flex" alignItems="center" justifyContent="space-between" py={1}>
                <Typography variant="body2" noWrap style={{ maxWidth: 300 }}>
                  {item.file.name}
                </Typography>
                {item.status === "extracting" ? (
                  <Typography variant="body2" color="primary">
                    {t("form.extracting")}
                  </Typography>
                ) : item.status === "extracted" ? (
                  <CheckCircle style={{ color: green[500], fontSize: 20 }} />
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    ...
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>
    );
  }

  return (
    <DropZone
      elevation={0}
      $isDragOver={isDragOver}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
    >
      <Box p={8} textAlign="center">
        <CloudUpload style={{ fontSize: 64, color: grey[500], marginBottom: 16 }} />
        <Typography variant="h6" gutterBottom>
          {t("form.batch_drop_files")}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {t("form.batch_drop_hint")}
        </Typography>
      </Box>
      <input ref={inputRef} type="file" accept=".pdf" multiple hidden onChange={handleInputChange} />
    </DropZone>
  );
};
